import type { PartOutcome, PartTarget } from './wide-replace'

/**
 * Continuous Find over a wide scope (AB#366): the "this artefact" / "all artefacts" scopes step
 * through every match of every part, in one order — the artefacts in the bid's tree order, the parts
 * in their artefact's order, and document order inside a part (the open part's own matcher). Pure:
 * the panel (components/FindReplace.tsx) feeds it the background count and the open part's live one.
 */

/** One part of the scope, in scope order, and how many matches it holds. */
export interface PartCount {
  room: string
  /** The artefact's id (the room key's middle segment). */
  artefact: string
  artefactTitle: string
  count: number
}

/** The artefact of a room key ("bid/artefact/part"). */
export const artefactOf = (room: string): string => room.split('/')[1] ?? ''

/** What a count-only outcome says a part holds: its matches, 0 for anything else. */
const matchedCount = (o: PartOutcome | undefined): number => (o?.status === 'matched' ? o.count : 0)

/**
 * The scope's parts, in order, with their counts. `live` replaces the open room's background count
 * with what its editor finds now: the editor matches the RENDERED text (the background count, the raw
 * markdown), and a teammate may have typed since — the counter and the steps must agree with what ↑/↓
 * actually lands on.
 */
export function partCounts(targets: readonly PartTarget[], outcomes: readonly PartOutcome[], live?: { room: string; count: number }): PartCount[] {
  const byRoom = new Map(outcomes.map((o) => [o.room, o]))
  return targets.map((t) => ({
    room: t.room,
    artefact: t.artefact ?? artefactOf(t.room),
    artefactTitle: t.artefactTitle ?? t.title,
    count: live && live.room === t.room ? live.count : matchedCount(byRoom.get(t.room)),
  }))
}

/** Every match of the scope. */
export const totalOf = (counts: readonly PartCount[]): number => counts.reduce((n, c) => n + c.count, 0)

/** The 0-based global position of the open room's `local`-th match; null when it is not in the scope or has no such match. */
export function globalIndex(counts: readonly PartCount[], room: string, local: number): number | null {
  const at = counts.findIndex((c) => c.room === room)
  if (at < 0 || local < 0 || local >= counts[at]!.count) return null
  return counts.slice(0, at).reduce((n, c) => n + c.count, 0) + local
}

/**
 * The part ↓ (dir 1) or ↑ (dir -1) goes to once the open room's matches are used up: the next (or
 * previous) part holding a match, wrapping at both ends of the scope — the open room itself when it
 * is the only one. A room outside the scope (a grid part) starts from the scope's first / last part.
 * null: nothing matches anywhere.
 */
export function stepRoom(counts: readonly PartCount[], room: string, dir: 1 | -1): string | null {
  const n = counts.length
  if (n === 0) return null
  const at = counts.findIndex((c) => c.room === room)
  const start = at < 0 ? (dir === 1 ? -1 : n) : at
  for (let k = 1; k <= n; k++) {
    const c = counts[(((start + dir * k) % n) + n) % n]!
    if (c.count > 0) return c.room
  }
  return null
}

/** One row of the results: an artefact, its parts summed. */
export interface ArtefactSum {
  artefact: string
  title: string
  /** Matches found (a count) — or, for a Replace all report, replaced. */
  count: number
  /** Matches left alone because another member holds them (a deck). */
  skipped: number
  /** Parts a lock kept entirely out of a Replace all. */
  locked: number
  /** Parts that could not be opened. */
  failed: number
  /** Whether any part of it was a report's `replaced` (else: a count). */
  replaced: boolean
}

/** The outcomes of a count or of a Replace all, one row per artefact, in scope order. `live`: the
 *  open room's own count, as in {@link partCounts}. */
export function sumByArtefact(targets: readonly PartTarget[], outcomes: readonly PartOutcome[], live?: { room: string; count: number }): ArtefactSum[] {
  const byRoom = new Map<string, PartOutcome>(outcomes.map((o) => [o.room, o]))
  if (live) byRoom.set(live.room, { room: live.room, title: '', status: 'matched', count: live.count })
  const rows = new Map<string, ArtefactSum>()
  for (const t of targets) {
    const artefact = t.artefact ?? artefactOf(t.room)
    let row = rows.get(artefact)
    if (!row) {
      row = { artefact, title: t.artefactTitle ?? t.title, count: 0, skipped: 0, locked: 0, failed: 0, replaced: false }
      rows.set(artefact, row)
    }
    const o = byRoom.get(t.room)
    if (!o) continue
    if (o.status === 'matched') row.count += o.count
    else if (o.status === 'replaced') {
      row.count += o.count
      row.skipped += o.skipped ?? 0
      row.replaced = true
    } else if (o.status === 'locked') row.locked += 1
    else if (o.status === 'failed') row.failed += 1
  }
  return [...rows.values()]
}
